/**
 * ExpandedEditorModal
 * Full-screen modal for editing skill instructions with markdown support
 */

import React from 'react';
import { X, Minimize2 } from 'lucide-react';
import { Button, Textarea } from '../../ui';
import styles from './ExpandedEditorModal.module.css';

export interface ExpandedEditorModalProps {
  isOpen: boolean;
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  maxLength?: number;
}

export const ExpandedEditorModal: React.FC<ExpandedEditorModalProps> = ({
  isOpen,
  value,
  onChange,
  onClose,
  maxLength = 30000,
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Edit Instructions</h2>
          <div className={styles.actions}>
            <button
              className={styles.iconButton}
              onClick={onClose}
              aria-label="Minimize editor"
              title="Minimize"
            >
              <Minimize2 size={20} />
            </button>
            <button
              className={styles.iconButton}
              onClick={onClose}
              aria-label="Close editor"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className={styles.content}>
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter instructions for the skill..."
            rows={25}
            maxLength={maxLength}
            className={styles.textarea}
            autoFocus
          />
          <div className={styles.footer}>
            <span className={styles.charCount}>
              {value.length} / {maxLength}
            </span>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpandedEditorModal;
